import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { getAutotestsRun, getAutotestsRunRounds, getAutotestsRunMessages } from '../../api/autotests'
import { useI18n } from '../../i18n'
import { FilterGroup, FiltersRow, PageCard, PageSection, Select } from '../../components/ui'
import { AccessDeniedView, DateCell, ExpandableText, MetaRow, StatusBadge } from './components'
import { asList, formatLocalDate, hasRunning, STATUS_ORDER } from './helpers'
import '../../styles/pages/page-common.css'
import '../../styles/pages/autotests.css'

const QUALITY_KEYS = ['naturalness', 'relevance', 'persona', 'tact', 'safety']

function getRoundStatus(round, runStatus) {
  const explicit = String(round?.status || '').toUpperCase()
  if (explicit) return explicit
  const run = String(runStatus || '').toUpperCase()
  if (run === 'RUNNING') return 'RUNNING'
  if (round?.passed === true) return 'PASSED'
  if (round?.passed === false) return 'FAILED'
  return run || 'RUNNING'
}

export default function AutotestsRunDetailPage() {
  const { runId } = useParams()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const autotestsHref = pathname.startsWith('/v2') ? '/v2/autotests' : '/old/autotests'

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)

  const [run, setRun] = useState(null)
  const [rounds, setRounds] = useState([])
  const [messages, setMessages] = useState([])
  const [statusFilter, setStatusFilter] = useState('')

  const statusOptions = useMemo(
    () => [{ value: '', label: t('logs.status.all') }, ...STATUS_ORDER.map((status) => ({ value: status, label: t(`autotests.status.${status}`) }))],
    [t],
  )

  const filteredRounds = useMemo(() => {
    if (!statusFilter) return rounds
    return rounds.filter((item) => getRoundStatus(item, run?.status) === statusFilter)
  }, [rounds, run?.status, statusFilter])

  const checksSummary = useMemo(() => {
    let passed = 0
    let failed = 0
    for (const round of rounds) {
      const assertions = round?.assertions
      if (!assertions || typeof assertions !== 'object') continue
      for (const value of Object.values(assertions)) {
        if (value === true) passed += 1
        else if (value === false) failed += 1
      }
    }
    return { passed, failed }
  }, [rounds])

  const isSameRun = useCallback((a, b) => {
    if (!a || !b) return a === b
    return (
      a.id === b.id
      && a.status === b.status
      && a.error === b.error
      && a.started_at === b.started_at
      && a.completed_at === b.completed_at
      && a.total_rounds === b.total_rounds
      && a.passed_checks === b.passed_checks
      && a.failed_checks === b.failed_checks
    )
  }, [])

  const isSameRounds = useCallback((prev, next) => {
    if (!Array.isArray(prev) || !Array.isArray(next)) return false
    if (prev.length !== next.length) return false
    for (let i = 0; i < prev.length; i += 1) {
      const a = prev[i]
      const b = next[i]
      if (!a || !b) return false
      if (
        a.id !== b.id
        || a.passed !== b.passed
        || a.bot_response !== b.bot_response
        || a.state_after !== b.state_after
        || a.duration_ms !== b.duration_ms
      ) {
        return false
      }
    }
    return true
  }, [])

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true)
      setError('')
      setAccessDenied(false)
    }

    try {
      const [runRes, roundsRes, messagesRes] = await Promise.all([
        getAutotestsRun(runId).catch(() => null),
        getAutotestsRunRounds(runId),
        getAutotestsRunMessages(runId).catch(() => null),
      ])
      setRun((prev) => (isSameRun(prev, runRes) ? prev : runRes))
      const nextRounds = asList(roundsRes)
      setRounds((prev) => (isSameRounds(prev, nextRounds) ? prev : nextRounds))
      setMessages(messagesRes?.data || [])
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDenied(true)
      } else {
        setError(err?.message || t('common.error'))
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [isSameRounds, isSameRun, runId, t])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const shouldPoll = String(run?.status || '').toUpperCase() === 'RUNNING' || hasRunning(rounds)
    if (!shouldPoll) return
    const timer = setTimeout(() => {
      loadData({ silent: true })
    }, 5000)
    return () => clearTimeout(timer)
  }, [loadData, run, rounds])

  if (accessDenied) return <AccessDeniedView />

  const totalRounds = run?.total_rounds ?? rounds.length
  const passedChecks = run?.passed_checks ?? checksSummary.passed
  const failedChecks = run?.failed_checks ?? checksSummary.failed
  const checksTotal = Math.max(passedChecks + failedChecks, 1)
  const passedPercent = Math.max(0, Math.min(100, Math.round((passedChecks / checksTotal) * 100)))

  return (
    <PageSection
      className="autotests-page"
      title={`Test Run #${run?.id || runId}`}
      subtitle={run?.scenario_name || run?.scenario_id || t('autotests.runDetail.subtitle')}
      actions={<Link className="autotests-link" to={autotestsHref}>← {t('autotests.backToDashboard')}</Link>}
    >
      {error && <p className="form-error">{error}</p>}
      <PageCard className="autotests-panel">
        <div className="autotests-run-header-grid">
          <MetaRow label={t('autotests.status')} value={<StatusBadge status={run?.status} />} />
          <MetaRow label={t('autotests.date')} value={<DateCell iso={run?.created_at || run?.started_at} />} />
          <MetaRow label={t('autotests.scenario')} value={run?.scenario_name || run?.scenario_id || '—'} />
          <MetaRow label={t('autotests.rounds.total')} value={totalRounds} />
        </div>

        {run?.error && (
          <div className="autotests-meta-row" style={{ marginTop: '16px', color: 'var(--color-danger)' }}>
            <span className="autotests-meta-key">Error:</span>
            <span className="autotests-meta-value">{run.error}</span>
          </div>
        )}

        <div className="autotests-progress-block">
          <div className="autotests-progress-label">
            <span>{t('autotests.passedFailed')}</span>
            <strong>{passedChecks}/{failedChecks}</strong>
          </div>
          <div className="autotests-progress">
            <div className="autotests-progress-fill" style={{ width: `${passedPercent}%` }} />
          </div>
        </div>
      </PageCard>

      <FiltersRow>
        <FilterGroup label={t('autotests.filters.status')}>
          <Select
            className="filter-select"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || '')}
            options={statusOptions}
            dropdownPosition="down"
          />
        </FilterGroup>
      </FiltersRow>

      <div className="autotests-rounds">
        <h3 className="autotests-panel-title">{t('autotests.rounds.title')}</h3>
        {loading && rounds.length === 0 ? (
          <p className="autotests-muted">{t('common.loading')}</p>
        ) : filteredRounds.length === 0 ? (
          <p className="autotests-muted">{t('autotests.empty')}</p>
        ) : (
          <>
            {messages.length > 0 && (
              <PageCard className="autotests-chat-card">
                <h3 className="autotests-panel-title">{t('autotests.conversation')}</h3>
                <div className="autotests-chat">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`autotests-chat-msg ${msg.direction === 'in' ? 'autotests-chat-msg--fan' : 'autotests-chat-msg--bot'}`}>
                      <div className={`autotests-chat-bubble ${msg.direction === 'in' ? 'autotests-chat-bubble--fan' : 'autotests-chat-bubble--bot'}`}>
                        <div className="autotests-chat-role">{msg.direction === 'in' ? 'Fan' : 'Bot'}</div>
                        <div className="autotests-chat-text">{msg.text || msg.raw_data?.text || '—'}</div>
                        {msg.purchase_offer && (
                          <div className="autotests-chat-purchase">
                            <strong>${msg.purchase_offer.price || '—'}</strong>
                            {msg.purchase_offer.media_ids && <span> ({msg.purchase_offer.media_ids.join(', ')})</span>}
                          </div>
                        )}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="autotests-chat-attachments">
                            {msg.attachments.map((att, i) => (
                              <span key={i} className="autotests-tag">{att.media_type} #{att.media_id}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </PageCard>
            )}

            <h3 className="autotests-panel-title">{t('autotests.rounds.title')}</h3>
            {filteredRounds.map((round) => {
            const assertions = round?.assertions && typeof round.assertions === 'object' ? Object.entries(round.assertions) : []
            const quality = round?.quality_scores && typeof round.quality_scores === 'object' ? round.quality_scores : null

            return (
              <PageCard className="autotests-round-card" key={round.id || `${round.round_number}-${round.created_at}`}>
                <div className="autotests-round-head">
                  <h4>#{round.round_number ?? '—'}</h4>
                  <StatusBadge status={getRoundStatus(round, run?.status)} />
                </div>

                <div className="autotests-round-texts">
                  <div className="autotests-round-box autotests-round-box--fan">
                    <div className="autotests-muted">{t('autotests.form.fanMessage')}</div>
                    <ExpandableText text={round.fan_message} />
                  </div>
                  <div className="autotests-round-box autotests-round-box--bot">
                    <div className="autotests-muted">{t('autotests.form.botResponse')}</div>
                    {round.bot_response ? (
                      <ExpandableText text={round.bot_response} />
                    ) : (
                      <p className="autotests-muted">{t('autotests.rounds.noBotResponse')}</p>
                    )}
                    {round.purchase_offer && (
                      <div className="autotests-purchase-offer">
                        <span className="autotests-muted">Offer: </span>
                        <strong>${round.purchase_offer.price || '—'}</strong>
                        {round.purchase_offer.media_ids && (
                          <span className="autotests-muted"> (media: {round.purchase_offer.media_ids.join(', ')})</span>
                        )}
                      </div>
                    )}
                    {round.attachments && round.attachments.length > 0 && (
                      <div className="autotests-attachments">
                        <span className="autotests-muted">Attachments: </span>
                        {round.attachments.map((att, i) => (
                          <span key={i} className="autotests-tag">{att.media_type || 'photo'} #{att.media_id}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="autotests-meta-row">
                  <span className="autotests-meta-key">{t('autotests.form.stateTransition')}</span>
                  <span className="autotests-meta-value">{round.state_before || '—'} → {round.state_after || '—'}</span>
                </div>

                <div className="autotests-meta-row">
                  <span className="autotests-meta-key">{t('autotests.form.goalSelected')}</span>
                  <span className="autotests-tag">{round.goal_selected || '—'}</span>
                </div>

                <div>
                  <div className="autotests-muted">{t('autotests.form.qualityScores')}</div>
                  <div className="autotests-quality-grid">
                    {QUALITY_KEYS.map((key) => (
                      <div key={key} className="autotests-quality-item">
                        <span>{key}</span>
                        <strong>{quality?.[key] ?? '—'}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="autotests-muted">{t('autotests.form.assertions')}</div>
                  {assertions.length === 0 ? (
                    <p className="autotests-muted">—</p>
                  ) : (
                    <ul className="autotests-assertions">
                      {assertions.map(([key, value]) => (
                        <li key={key}>
                          <span>{value === true ? '✅' : value === false ? '❌' : '—'}</span>
                          <span>{key}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="autotests-meta-row">
                  <span className="autotests-meta-key">{t('autotests.form.durationMs')}</span>
                  <span className="autotests-meta-value">{round.duration_ms ?? '—'}</span>
                </div>

                <div className="autotests-muted">{formatLocalDate(round.created_at)}</div>
              </PageCard>
            )
          })}
          </>
        )}
      </div>
    </PageSection>
  )
}
